import React, { useState, useCallback } from 'react';
import { Search, Loader2, AlertCircle } from 'lucide-react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useSearchVendors, useSearchPurchaseOrders, useLinkMissingReference } from '@api/hooks/usePendingDocuments';
import type { MissingReferenceInfo, VendorSearchResponse, PurchaseOrderSearchResponse } from '@schemas/api';

export interface MissingReferenceResolution {
  referenceType: 'vendor' | 'purchase_order';
  entityIdentifier: string;
  linkedVendorId?: string;
  linkedVendorName?: string;
}

interface ReferenceSelectionModalProps {
  missingReference: MissingReferenceInfo;
  onSuccess: (resolution?: MissingReferenceResolution) => void;
  onClose: () => void;
}

export const ReferenceSelectionModal: React.FC<ReferenceSelectionModalProps> = ({
  missingReference,
  onSuccess,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  // Determine reference type
  const isVendorRef = missingReference.ref_object_type === 'vendor';
  const isPORef = missingReference.ref_object_type === "purchase_order";

  // Use appropriate search hook
  const vendorQuery = useSearchVendors(isVendorRef ? debouncedSearchTerm : '');
  const poQuery = useSearchPurchaseOrders(isPORef ? debouncedSearchTerm : '');
  const linkMissingRef = useLinkMissingReference();

  const vendorSearch = vendorQuery.data;
  const poSearch = poQuery.data;
  const isSearching = isVendorRef ? vendorQuery.isLoading : poQuery.isLoading;

  const results = isVendorRef ? vendorSearch?.items || [] : poSearch?.items || [];


  const handleSelectResult = useCallback(
    async (result: VendorSearchResponse | PurchaseOrderSearchResponse) => {
      try {
        if (!missingReference.id) {
          console.error('Missing reference ID is not set');
          return;
        }

        const resultIdentifier = isVendorRef
          ? (result as VendorSearchResponse).legal_name
          : (result as PurchaseOrderSearchResponse).po_number;

        await linkMissingRef.mutateAsync({
          referenceId: missingReference.id,
          objectType: isVendorRef ? 'vendor' : "purchase_order",
          objectId: resultIdentifier,
        });

        onSuccess({
          referenceType: isVendorRef ? 'vendor' : 'purchase_order',
          entityIdentifier: resultIdentifier,
          linkedVendorId: isVendorRef ? (result as VendorSearchResponse).id : undefined,
          linkedVendorName: isVendorRef ? (result as VendorSearchResponse).legal_name : undefined,
        });
      } catch (error) {
        console.error('Failed to link reference:', error);
      }
    },
    [missingReference.id, isVendorRef, linkMissingRef, onSuccess]
  );

  // Search mode
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-blue-50 border-b border-blue-200 p-4">
          <h2 className="text-lg font-semibold text-blue-900 mb-2">
            Search for {isVendorRef ? 'Vendor' : 'Purchase Order'}
          </h2>
          <p className="text-sm text-blue-700">
            Looking for: <span className="font-medium">{missingReference.ref_value}</span>
          </p>
        </div>

        {/* Search Input */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={isVendorRef ? 'Search vendor by name...' : 'Search PO by number...'}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {isSearching && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            </div>
          )}

          {!isSearching && results.length === 0 && searchTerm && (
            <div className="text-center py-8">
              <p className="text-gray-600 text-sm">No results found for "{searchTerm}"</p>
            </div>
          )}

          {!isSearching && results.length === 0 && !searchTerm && (
            <div className="text-center py-8">
              <p className="text-gray-600 text-sm">Start typing to search</p>
            </div>
          )}

          {results.map((result: VendorSearchResponse | PurchaseOrderSearchResponse) => {
            const identifier = isVendorRef
              ? (result as VendorSearchResponse).legal_name
              : (result as PurchaseOrderSearchResponse).po_number;
            if (!identifier) {
              return null;
            }
            return (
            <button
              key={identifier}
              onClick={() => handleSelectResult(result)}
              disabled={linkMissingRef.isPending}
              className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  {isVendorRef ? (
                    <>
                      <p className="font-medium text-gray-900">{(result as VendorSearchResponse).legal_name}</p>
                      <p className="text-xs text-gray-500">
                        Status: {(result as VendorSearchResponse).status} • Terms: {(result as VendorSearchResponse).default_payment_terms}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-gray-900">{(result as PurchaseOrderSearchResponse).po_number}</p>
                      <p className="text-xs text-gray-500">
                        {(result as PurchaseOrderSearchResponse).vendor_name} • {(result as PurchaseOrderSearchResponse).total} {(result as PurchaseOrderSearchResponse).currency}
                      </p>
                    </>
                  )}
                </div>
                {linkMissingRef.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600 ml-2" />
                )}
              </div>
            </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 space-y-3">
          <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-3">
            <div className="flex gap-2 items-start">
              <AlertCircle className="h-4 w-4 text-yellow-700 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-yellow-800">
                Can't find what you're looking for? Use the "Create" button in the missing references section to create a new one.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={linkMissingRef.isPending}
            className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferenceSelectionModal;
